import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  serverTimestamp,
  onSnapshot,
  writeBatch,
  arrayUnion,
  arrayRemove,
  deleteField,
  disableNetwork,
  enableNetwork,
} from 'firebase/firestore';
import app from './config';

const db = getFirestore(app);

export default db;

// Mobile browsers (iOS especially, and installed PWAs most of all) silently
// kill the Firestore listen stream while the app is backgrounded/the phone
// is locked, and the SDK can take a long time to notice — meanwhile every
// onSnapshot just sits there with stale data and no error to retry on, which
// is the "events/messages won't load until I refresh" symptom. Cycling the
// network on return forces a fresh stream immediately; listeners keep their
// current data (served from cache) while it reconnects, and queued writes
// are flushed afterwards, so nothing is lost. Returns an uninstaller.
const RECONNECT_AFTER_HIDDEN_MS = 30000;
export function installResumeReconnect() {
  if (typeof document === 'undefined') return () => {};
  let hiddenAt = document.hidden ? Date.now() : null;
  let cycling = false;
  const cycle = async () => {
    if (cycling) return;
    cycling = true;
    try {
      await disableNetwork(db);
      await enableNetwork(db);
    } catch (err) {
      console.warn('Firestore reconnect failed:', err?.message || err);
      enableNetwork(db).catch(() => {});
    } finally {
      cycling = false;
    }
  };
  const onVisibility = () => {
    if (document.hidden) { hiddenAt = Date.now(); return; }
    const awayMs = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = null;
    if (awayMs >= RECONNECT_AFTER_HIDDEN_MS) cycle();
  };
  const onOnline = () => cycle();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', onOnline);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('online', onOnline);
  };
}

export {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  serverTimestamp,
  onSnapshot,
  writeBatch,
  arrayUnion,
  arrayRemove,
  deleteField,
};
